import { useState, useReducer, useEffect, useCallback } from "react";
import { PayloadAction, createSlice } from "redux-starter-kit";
import LoadingState from "./LoadingState";

interface UseAsyncState<T, TError> {
  loadingState: LoadingState;
  response?: T;
  error?: TError;
}

interface UseAsyncActions {
  execute: () => void;
  reset: () => void;
}

const initialState: UseAsyncState<unknown, unknown> = {
  loadingState: LoadingState.NO_DATA,
  error: undefined,
  response: undefined,
};

/*
 * Reducer + actions for useAsync hook.
 */
const useAsyncSlice = createSlice({
  initialState,
  reducers: {
    startAsync(state) {
      state.loadingState = LoadingState.LOADING;
    },
    asyncResponse(state, action: PayloadAction<unknown>) {
      state.loadingState = LoadingState.RESOLVED;
      state.error = undefined;
      state.response = action.payload;
    },
    asyncError(state, action: PayloadAction<unknown>) {
      state.loadingState = LoadingState.REJECTED;
      state.error = action.payload;
      state.response = undefined;
    },
    resetAsync(state) {
      state.error = undefined;
      state.response = undefined;
      state.loadingState = LoadingState.NO_DATA;
    },
  },
});

const { actions: actionCreators } = useAsyncSlice;
const { startAsync, asyncResponse, asyncError, resetAsync } = actionCreators;

/**
 * Hook to use asynchronous functions in a component. More generic than useHttpRequest.
 * TODO: useHttpRequest should be refactored to wrap this hook.
 * TODO: Possible memory leak - Warning: Can't perform a React state update on an unmounted component. This is a no-op, but it indicates a memory leak in your application. To fix, cancel all subscriptions and asynchronous tasks in a useEffect cleanup function.
 * Only saw above warning once, not sure it is from this hook.
 *
 * @template T
 * @template TError
 * @template TFunctionParams
 * @param {(...args: TFunctionParams) => Promise<T>} asyncFunction
 * @param {TFunctionParams} functionArguments
 * @returns {(UseAsyncState<T, TError> & UseAsyncActions)}
 */
function useAsync<
  T,
  TError = unknown,
  TFunctionParams extends unknown[] = unknown[]
>(
  asyncFunction: (...arguments_: TFunctionParams) => Promise<T>,
  functionArguments: TFunctionParams
): UseAsyncState<T, TError> & UseAsyncActions {
  const [currentArguments, setCurrentArguments] = useState<TFunctionParams>();
  const [state, dispatch] = useReducer(useAsyncSlice.reducer, initialState);

  useEffect(() => {
    let didCancel = false;

    const executeAsyncFn = async (): Promise<void> => {
      if (!asyncFunction || currentArguments === undefined) {
        return;
      }

      dispatch(startAsync());

      try {
        const response = await asyncFunction(...currentArguments);

        if (!didCancel) {
          dispatch(asyncResponse(response));
        }
      } catch (error) {
        if (!didCancel) {
          dispatch(asyncError(error));
        }
      }
    };

    executeAsyncFn();

    return () => {
      didCancel = true;
    };
  }, [asyncFunction, currentArguments]);

  const execute = useCallback(() => {
    setCurrentArguments([...functionArguments] as TFunctionParams);

    // Ignore eslint warning for good reason.
    // Basically, array needs to be passed like this to avoid infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, functionArguments);

  const reset = useCallback(() => {
    dispatch(resetAsync());
  }, []);

  return { ...(state as UseAsyncState<T, TError>), execute, reset };
}

export default useAsync;
